/**
 * Is what this laptop uploaded still in Firebase? (apps/octopus/docs/sync-plan.md, Step 5)
 *
 * After the Relay tab uploads, and every few minutes while it is open, the records this
 * laptop marked as uploaded are looked up again in their match documents:
 *
 *   deleted  - the slot is gone: someone deleted it in Firebase. Shown, never re-uploaded
 *              on its own - a mistaken delete is a person's call to undo, not the laptop's.
 *   changed  - the slot differs from what was sent: someone edited it in Firebase. That
 *              edit stands (an edit is a correction, wherever it is made).
 *   ok       - still exactly what was sent.
 *
 * One read per match document, not per record. Only the new layout (normal_data /
 * timekeeper_data); 2025 and 9999 used another and are not checked. The paths are the
 * ones Relay.js's writeToFirestore and uploadObject write.
 */
import { SUBMISSION_ID_KEY } from './submissionId';

const FIELDS = { scouting: 'normal_data', timekeeper: 'timekeeper_data' };
const LOCAL_ONLY = ['id', '_queued_at', '_gamemode', '_stream', 'version', 'gamemode', SUBMISSION_ID_KEY];
const LEGACY_YEARS = [2025, 9999];

function checksKey(eventPath) {
    return `firebase_check_${eventPath || 'GLOBAL'}`;
}

/** Where a queued record lives in Firebase, or null if it can't be placed or checked. */
export function slotOf(record, eventPath, stream = 'scouting') {
    const year = parseInt(String(eventPath || '').substring(0, 4));
    if (!year || LEGACY_YEARS.includes(year)) return null;
    if (!record.match_number || !record.alliance_color || record.scout_team == null || record.scout_id == null) return null;
    const gamemode = record._gamemode ?? 2;
    const matchKey = `${parseInt(record.match_number) + (gamemode + 1) * 1000}-${record.alliance_color}`;
    return {
        path: ['Competitions', 'Years', String(year), String(eventPath).substring(4), 'Final', matchKey],
        field: FIELDS[stream] || FIELDS.scouting,
        slot: `${record.scout_team}-${record.scout_id}-${record.alliance_color}${record.team_position}`,
    };
}

/** A record's content for comparison: no local-only fields, blanks ignored, true === 1. */
function comparable(record) {
    const out = {};
    for (const [k, v] of Object.entries(record || {})) {
        if (LOCAL_ONLY.includes(k) || v === null || v === undefined || v === '') continue;
        out[k] = typeof v === 'boolean' ? Number(v) : v;
    }
    return JSON.stringify(Object.keys(out).sort().map(k => [k, out[k]]));
}

/**
 * @param {Array<object>} records  queued records already uploaded, each with `_stream`
 * @param {string} eventPath
 * @param {(path: string[]) => Promise<object|null>} readDoc  the match document's data, or null
 * @returns {Promise<Object<string, 'deleted'|'changed'|'ok'>>} by record id; a record whose
 *          document could not be read is left out rather than guessed at
 */
export async function checkUploaded(records, eventPath, readDoc) {
    const byDoc = new Map();
    for (const record of records) {
        const where = slotOf(record, eventPath, record._stream);
        if (!where) continue;
        const key = where.path.join('/');
        if (!byDoc.has(key)) byDoc.set(key, { path: where.path, items: [] });
        byDoc.get(key).items.push({ record, where });
    }

    const results = {};
    await Promise.all([...byDoc.values()].map(async ({ path, items }) => {
        let data;
        try {
            data = await readDoc(path);
        } catch {
            return; // offline, or not allowed: say nothing rather than "deleted"
        }
        for (const { record, where } of items) {
            const remote = data && data[where.field] ? data[where.field][where.slot] : undefined;
            if (remote === undefined) results[record.id] = 'deleted';
            else results[record.id] = comparable(remote) === comparable(record) ? 'ok' : 'changed';
        }
    }));
    return results;
}

export function getChecks(eventPath) {
    try {
        return JSON.parse(localStorage.getItem(checksKey(eventPath)) || '{}');
    } catch {
        return {};
    }
}

/** Merge new results in, stamped with when they were seen. */
export function saveChecks(eventPath, results, now = Date.now()) {
    const all = getChecks(eventPath);
    for (const [id, state] of Object.entries(results)) all[id] = { state, at: now };
    try {
        localStorage.setItem(checksKey(eventPath), JSON.stringify(all));
    } catch {}
    return all;
}
